import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, assetUrl, errMsg } from "../../lib/api";
import { useAdminGuard, AdminTop } from "./AdminDashboard";

const CT_ZONE = "America/Chicago";
// auction_date is stored as UTC ISO ("...Z"); the datetime-local input speaks Central wall time
const ctParts = (utcMs) => {
  const o = {};
  for (const p of new Intl.DateTimeFormat("en-US", { timeZone: CT_ZONE, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(utcMs))) o[p.type] = p.value;
  return o;
};
const utcToInput = (iso) => {
  if (!iso) return "";
  if (!iso.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(iso)) return iso.slice(0, 16); // legacy naive = already CT wall
  const p = ctParts(new Date(iso).getTime());
  return `${p.year}-${p.month}-${p.day}T${String(+p.hour % 24).padStart(2, "0")}:${p.minute}`;
};
const inputToUtc = (val) => {
  if (!val) return val;
  const [dp, tp] = val.split("T");
  const [y, m, d] = dp.split("-").map(Number);
  const [hh, mm] = (tp || "00:00").split(":").map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  const off = (w) => { const p = ctParts(w); return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute) - w; };
  const utc = wall - off(wall - off(wall)); // two passes for DST-boundary correctness
  return new Date(utc).toISOString().slice(0, 19) + "Z";
};

export default function AuctionForm() {

  const ready = useAdminGuard();
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    title: "", auction_date: "", location: "", description: "",
    image: "", external_link: "", featured: false, gallery: [],
  });

  useEffect(() => {
    if (!ready) return;
    api.get("/locations").then((r) => setLocations(r.data));
    if (!isNew) {
      api.get(`/auctions/${id}`).then((r) => {
        const a = r.data;
        setF({
          title: a.title || "", auction_date: utcToInput(a.auction_date || ""),
          location: a.location || "", description: a.description || "",
          image: a.image || "", external_link: a.external_link || "",
          featured: !!a.featured, gallery: a.gallery || [],
        });
      }).catch(() => setError("Auction not found."));
    }
  }, [ready, id, isNew]);

  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const uploadCover = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((s) => ({ ...s, image: data.url }));
    } catch (err) { setError(errMsg(err)); }
    setUploading(false);
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) { const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd); urls.push(data.url); }
      setF((s) => ({ ...s, gallery: [...s.gallery, ...urls] }));
    } catch (err) { setError(errMsg(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const removeGallery = (url) => setF((s) => ({ ...s, gallery: s.gallery.filter((g) => g !== url) }));

  const dragIdx = useRef(null);
  const reorder = (to) => {
    const from = dragIdx.current;
    if (from === null || from === to) return;
    setF((s) => {
      const g = [...s.gallery];
      const [moved] = g.splice(from, 1);
      g.splice(to, 0, moved);
      return { ...s, gallery: g };
    });
    dragIdx.current = null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.auction_date || !f.location) { setError("Title, date and location are required."); return; }
    setBusy(true); setError(null);
    try {
      if (isNew) await api.post("/admin/auctions", { ...f, auction_date: inputToUtc(f.auction_date) });
      else await api.put(`/admin/auctions/${id}`, { ...f, auction_date: inputToUtc(f.auction_date) });
      navigate("/admin/dashboard");
    } catch (err) { setError(errMsg(err)); setBusy(false); }
  };

  if (!ready) return <div className="loading">Loading…</div>;

  return (
    <div className="adm">
      <AdminTop />
      <main className="admin-wrap narrow">
        <button className="btn-mini" onClick={() => navigate("/admin/dashboard")} style={{ width: "fit-content" }}>← Back to dashboard</button>
        <h1>{isNew ? "New auction" : "Edit auction"}</h1>
        {error && <div className="msg err">{error}</div>}
        <form onSubmit={submit} data-testid="auction-form">
          <label>Title <input type="text" value={f.title} onChange={on("title")} required data-testid="auction-title" /></label>
          <div className="form-two">
            <label>Date &amp; time <span className="muted" style={{ fontSize: ".72rem" }}>(Central Time)</span> <input type="datetime-local" value={f.auction_date} onChange={on("auction_date")} required data-testid="auction-date" /></label>
            <label>Location
              <input type="text" list="loclist" value={f.location} onChange={on("location")} required data-testid="auction-location" />
              <datalist id="loclist">{locations.map((l) => <option key={l.id} value={l.name} />)}</datalist>
            </label>
          </div>
          <label>Description <textarea rows={5} value={f.description} onChange={on("description")} data-testid="auction-description" /></label>
          <label>External link (optional bidding/details URL)
            <input type="url" value={f.external_link} onChange={on("external_link")} placeholder="https://" data-testid="auction-link" /></label>

          <label>Cover image
            {assetUrl(f.image) && <img className="thumb big" src={assetUrl(f.image)} alt="" />}
            <input type="file" accept="image/*" onChange={uploadCover} data-testid="auction-image" />
          </label>

          <div>
            <span className="hlbl" style={{ color: "var(--muted)", fontSize: ".82rem" }}>Photo gallery (shown on the auction's detail page)</span>
            {f.gallery.length > 1 && <span className="muted" style={{ display: "block", fontSize: ".75rem", marginTop: 4 }}>Drag photos to reorder how buyers see them.</span>}
            {f.gallery.length > 0 && (
              <div className="gallery-thumbs" data-testid="gallery-thumbs">
                {f.gallery.map((g, idx) => (
                  <div className="gthumb" key={g} draggable
                    onDragStart={() => (dragIdx.current = idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorder(idx)}
                    title="Drag to reorder" data-testid={`gallery-item-${idx}`}>
                    <img src={assetUrl(g)} alt="" />
                    <button type="button" className="gdel" onClick={() => removeGallery(g)} data-testid="gallery-del">✕</button>
                  </div>
                ))}
              </div>
            )}
            <input type="file" accept="image/*" multiple onChange={uploadGallery} data-testid="auction-gallery" />
          </div>

          <label className="check" style={{ margin: "18px 0" }}>
            <input type="checkbox" checked={f.featured} onChange={(e) => setF((s) => ({ ...s, featured: e.target.checked }))} data-testid="auction-featured" />
            Feature this auction (large hero card)
          </label>

          <button type="submit" className="btn-primary" disabled={busy || uploading} data-testid="auction-save">
            {uploading ? "Uploading…" : busy ? "Saving…" : "Save auction"}
          </button>
        </form>
      </main>
    </div>
  );
}
